import React, { useState, useEffect } from "react";
import axios from "axios";
// import { Tooltip } from "@mui/material"; // Unused in new design
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { imageBaseURL, Base_URL } from "actions/Base_URL";

export default function PortalImages() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadType, setUploadType] = useState("slide");
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${Base_URL}/api/images`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data && Array.isArray(response.data.images)) {
        setImages(response.data.images);
      } else if (Array.isArray(response.data)) {
        setImages(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setImages(response.data.data);
      } else {
        console.log("Unexpected response format", response.data);
        setImages([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching images", error);
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire({
        title: "Error",
        text: "Please select a file to upload.",
        icon: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("images", selectedFile);
    formData.append("type", uploadType);

    try {
      // API: https://api.nvcti.in/api/images/upload
      // Note: The prompt mentioned "upload button post and type api https://api.nvcti.in/api/images/upload"
      // and "https://api.nvcti.in/api/images/images?type=logo" which seems like a filter or another get?
      // But for upload, usually it's POST /upload

      const token = localStorage.getItem("token"); // Assuming auth is needed

      const response = await axios.post(`${Base_URL}/api/images/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Include if needed, often expected
        },
      });

      if (response.data) {
        Swal.fire({
          title: "Success",
          text: "Image uploaded successfully!",
          icon: "success",
        });
        setSelectedFile(null);
        // Reset file input value manually if needed, or rely on state
        document.getElementById("fileInput").value = "";
        fetchImages();
      }
    } catch (error) {
      console.error("Upload error", error);
      Swal.fire({
        title: "Error",
        text: "Failed to upload image.",
        icon: "error",
      });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this image?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.delete(`${Base_URL}/api/images/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          Swal.fire("Deleted!", "Image has been deleted.", "success");
          fetchImages(); // Refresh the list
        } catch (error) {
          console.error("Delete error", error);
          Swal.fire("Error!", "Failed to delete image.", "error");
        }
      }
    });
  };

  return (
    <div className="relative bg-blueGray-800 min-h-screen p-8">

      {/* Upload Section */}
      <div className="bg-blueGray-700 p-6 rounded-lg shadow-lg mb-8 max-w-4xl mx-auto">
        <h2 className="text-white text-xl font-bold mb-4">Upload New Image</h2>
        <div className="flex flex-col md:flex-row gap-4 items-center">

          {/* Type Dropdown */}
          <div className="w-full md:w-1/4">
            <select
              className="w-full px-3 py-2 text-blueGray-700 bg-white rounded border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-lightBlue-500"
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
            >
              <option value="slide">Slide</option>
              <option value="logo">Logo</option>
              <option value="logo-right">Logo Right</option>
              <option value="about-nvcti">About NVCTI</option>
              <option value="mechanical">Mechanical & Rapid Prototyping Unit</option>
              <option value="electrical">Electronics and IoT Unit</option>
              <option value="animation">Animation & Game Design Unit</option>
              <option value="pouch">Pouch Battery Cell Fabrication Unit</option>
            </select>
          </div>

          {/* File Input */}
          <div className="w-full md:w-1/2">
            <div className="relative">
              <input
                id="fileInput"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*"
              />
              <label
                htmlFor="fileInput"
                className="flex items-center justify-between w-full px-3 py-2 bg-blueGray-600 text-white rounded cursor-pointer border border-blueGray-500 hover:bg-blueGray-500 transition-colors"
              >
                <span className="truncate">
                  {selectedFile ? selectedFile.name : "No file chosen..."}
                </span>
                <span className="bg-blueGray-800 px-2 py-1 rounded text-xs ml-2">Browse</span>
              </label>
            </div>
          </div>

          {/* Upload Button */}
          <div className="w-full md:w-1/4">
            <button
              className="w-full bg-lightBlue-500 text-white font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 active:bg-lightBlue-600"
              type="button"
              onClick={handleUpload}
            >
              Upload Image
            </button>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images && images.length > 0 ? (
            images.map((img, index) => (
              <div key={img.image_id || index} className="relative bg-blueGray-700 rounded-lg shadow-lg overflow-hidden group">
                <img
                  src={`${imageBaseURL}${img.path}`}
                  alt={`Portal Image ${index}`}
                  className="w-full h-64 object-cover"
                  onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=Image+Not+Found"; }}
                />

                {/* Overlay with Type and Delete */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-3 flex justify-between items-center">
                  <span className="text-white text-sm font-semibold capitalize">Type: {img.type || "unknown"}</span>
                  <button
                    onClick={() => handleDelete(img.image_id)}
                    className="text-white hover:text-red-500 transition-colors focus:outline-none"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-10 text-white opacity-70">
              {loading ? "Loading images..." : "No images found."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
